import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Row, Col, Typography, Button, Divider, Spin, Table, App } from "antd";
import { PrinterOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useReactToPrint } from "react-to-print";
import ReportDom from "../../../components/common/ReportDom";
import { replaceRecordWithDetailsApi } from "../../../apiservice/sales";
import { convertNumberToWords, formatToCompanyTimezone } from "../../../lib/functions";

const { Title } = Typography;

const ProductReplaceVoucherQuick = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const authInfo = useSelector((s) => s.auth.auth_info);
  const { message } = App.useApp();

  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);

  const reportRef = useRef();
  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `ProductReplace_Voucher_${voucher?.voucher_no || "Voucher"}`,
  });

  useEffect(() => {
    if (id) fetchVoucher();
  }, [id]);

  const fetchVoucher = async () => {
    try {
      setLoading(true);
      const res = await replaceRecordWithDetailsApi({ replace_id: id });
      if (res?.data?.length > 0) {
        setVoucher(res.data[0]);
      } else {
        message.warning("No Product Replace Voucher data found.");
      }
    } catch (err) {
      message.error("Failed to load voucher.");
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <div style={{ textAlign: "center", paddingTop: 100 }}>
        <Spin size="large" />
      </div>
    );

  if (!voucher)
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>No Voucher Found</Title>
        <Button onClick={() => navigate("/product-replace")}>Back</Button>
      </div>
    );

  const givenItems = voucher.miDetails || [];
  const returnItems = voucher.mciDetails || [];

  const sum = (arr, fn) => arr.reduce((s, x) => s + (Number(fn(x)) || 0), 0);

  const givenTotal = sum(givenItems, (r) => r.item_total);
  const returnTotal = sum(returnItems, (r) => r.return_item_total);
  const returnVat = Number(voucher.return_total_tax || 0);
  const others = Number(voucher.others_cost || 0);

  // Correct total formula
  const computedTotal = (givenTotal - returnTotal - returnVat) + others;

  // Prefer backend total if present
  const totalCost = voucher.total_cost != null ? Number(voucher.total_cost) : computedTotal;

  // ---------------- TABLES ----------------
  const returnColumns = [
    { title: "Returned Product", dataIndex: "item_name" },
    { title: "Qty", dataIndex: "return_item_qty", align: "right" },
    { title: "Rate", dataIndex: "return_rate", align: "right", render: (t) => Number(t).toFixed(2) },
    { title: "Amount", align: "right", render: (r) => Number(r.return_item_total).toFixed(2) },
  ];

  const givenColumns = [
    { title: "Given Product", dataIndex: "item_name" },
    { title: "Qty", dataIndex: "item_qty", align: "right" },
    { title: "Rate", dataIndex: "item_rate", align: "right", render: (t) => Number(t).toFixed(2) },
    { title: "Amount", align: "right", render: (r) => Number(r.item_total).toFixed(2) },
  ];

  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/product-replace")}>Back</Button></Col>
        <Col><Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>Print</Button></Col>
      </Row>

      <div style={{ background: "white", padding: 24, borderRadius: 8 }}>
        <Title level={4} style={{ textAlign: "center" }}>PRODUCT REPLACE VOUCHER</Title>
        <Divider />

        <Row gutter={24}>
          <Col span={12}>
            <p><b>Voucher No:</b> {voucher.voucher_no}</p>
            <p><b>Date:</b> {formatToCompanyTimezone(voucher.created_date).split(" ")[0]}</p>
            <p><b>Customer:</b> {voucher.full_name}</p>
          </Col>
          <Col span={12}>
            <p><b>Branch:</b> {voucher.branch_name || voucher.branch_id}</p>
            <p><b>Contact:</b> {voucher.contact_no || "-"}</p>
          </Col>
        </Row>

        <Divider />

        <Title level={5}>Returned Items</Title>
        <Table columns={returnColumns} dataSource={returnItems} pagination={false} bordered size="small" />

        <Divider />

        <Title level={5}>Given Items</Title>
        <Table columns={givenColumns} dataSource={givenItems} pagination={false} bordered size="small" />

        <Divider />

        <table style={{ width: "100%" }}>
          <tbody>
            <tr>
              <td><b>Additional Expenses :</b></td>
              <td align="right">{others.toFixed(2)}</td>
            </tr>
            <tr><td colSpan={2}><hr /></td></tr>
            <tr>
              <td><b>Total ({authInfo?.currency || "INR"}):</b></td>
              <td align="right">{totalCost.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>

        <Divider />

        <p><b>In Words:</b> {convertNumberToWords(totalCost, authInfo?.currency || "AED")}</p>
        <p><b>Narration:</b> {voucher.narration || "-"}</p>
      </div>

      {/* PRINT VERSION */}
      <div style={{ position: "absolute", top: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="PRODUCT REPLACE VOUCHER"
          extraSections={[
            {
              title: "Summary",
              columns: [
                { title: "Additional Expenses", dataIndex: "other" },
                { title: "Total", dataIndex: "total" },
              ],
              data: [
                { other: others.toFixed(2), total: totalCost.toFixed(2) },
              ],
            },
          ]}
        />
      </div>
    </div>
  );
};

export default ProductReplaceVoucherQuick;
